/**
 * React Query hooks for Earthworks EW-4 (fieldiq-api#1284): daily logs,
 * rent-ins, locates, permits, hazmat, warnings, and the /field surface.
 *
 * Same rules as useEarthworks: every key starts ['earthworks'], every write
 * invalidates that one prefix, every read spreads LOAD_FAILURE_QUERY_OPTIONS
 * so a refusal reads as a refusal, never as an empty list.
 *
 * THE /field SURFACE IS SNAKE_CASE, meta included (`meta.today`,
 * `meta.units.volume`), so it has its own unwrap.
 *
 * FILES ARE ONLINE ONLY. Every Earthworks file is an `ew_job` upload presigned
 * against the JOB id, except a locate sheet, which is an `ew_site` upload
 * presigned against the SITE (a locate is the site's). Photos are compressed
 * on the phone first (lib/offline/image-compress, the PhotoUploader helper); `uploadEwJobFile` presigns, PUTs (the chokepoint checks
 * `res.ok`), confirms, and only then returns the upload id. Each step throws,
 * so nothing reads as attached unless the bucket and the API both said yes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { putToPresignedUrl } from '../api/upload';
import { compressImage } from '../lib/offline/image-compress';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { ewApiError } from '../lib/earthworks';
import type {
  EwDailyLog,
  EwDailyLogSummary,
  EwDailyLogWrite,
  EwFieldJob,
  EwFieldJobDetail,
  EwFieldLog,
  EwFieldLogSaved,
  EwFieldMeta,
  EwFieldResult,
  EwFieldWarning,
  EwHazmat,
  EwHazmatWrite,
  EwLocate,
  EwLocateWrite,
  EwPermit,
  EwPermitWrite,
  EwPreUseCheck,
  EwRentIn,
  EwRentInWrite,
  EwWarning,
  EwWarningAck,
} from '../types/earthworks';
import { invalidateEw, unwrapEw, unwrapEwList } from './useEarthworks';

const EMPTY_FIELD_META: EwFieldMeta = {
  unit_system: null,
  unit_system_source: null,
  units: null,
  currency: null,
  country: null,
  holdback_label: null,
  time_zone: null,
  today: null,
};

/** A /field body: payload and SNAKE meta. `data: null` stays null — never the envelope. */
export function unwrapEwField<T>(body: unknown): EwFieldResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_FIELD_META };
  const b = body as { data?: unknown; meta?: Partial<EwFieldMeta> };
  const data = 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_FIELD_META, ...(b.meta ?? {}) } };
}

export const ewFieldKeys = {
  myJobs: () => ['earthworks', 'field', 'myJobs'] as const,
  myJob: (id: string) => ['earthworks', 'field', 'myJob', id] as const,
  log: (jobId: string, day: string) => ['earthworks', 'field', 'log', jobId, day] as const,
  checks: (jobId: string, day: string) => ['earthworks', 'preUseChecks', jobId, day] as const,
  logs: (jobId: string) => ['earthworks', 'dailyLogs', jobId] as const,
  dailyLog: (id: string) => ['earthworks', 'dailyLog', id] as const,
  rentIns: (jobId: string) => ['earthworks', 'rentIns', jobId] as const,
  locates: (siteId: string) => ['earthworks', 'locates', siteId] as const,
  permits: (jobId: string) => ['earthworks', 'permits', jobId] as const,
  hazmat: (jobId: string) => ['earthworks', 'hazmat', jobId] as const,
  warnings: (jobId: string) => ['earthworks', 'warnings', jobId] as const,
  acks: (jobId: string) => ['earthworks', 'warningAcks', jobId] as const,
};

function useEwWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  // Awaited, so a screen that rebuilds its form on success reads the fresh log.
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateEw(qc) });
}

// ─── Files ───────────────────────────────────────────────────────────────────

/** Image types the canvas cannot be trusted to re-encode are sent as taken. */
const NO_COMPRESS = new Set(['image/heic', 'image/heif', 'image/gif', 'image/svg+xml']);

/**
 * A phone photo shrunk to a field-sized JPEG (longest side 1920, quality 0.7),
 * or the file as taken when it is not an image, cannot be decoded, or would not
 * get smaller. A PDF locate sheet is never touched.
 */
export async function prepareEwUpload(file: File): Promise<{ body: Blob; contentType: string; fileName: string }> {
  const contentType = file.type || 'application/octet-stream';
  const fileName = file.name || 'photo.jpg';
  if (contentType.startsWith('image/') && !NO_COMPRESS.has(contentType.toLowerCase())) {
    try {
      const small = await compressImage(file);
      if (small.size > 0 && small.size < file.size) {
        return { body: small, contentType: 'image/jpeg', fileName: fileName.replace(/\.[^.]+$/, '') + '.jpg' };
      }
    } catch {
      // Undecodable here — send it as taken; the API accepts it.
    }
  }
  return { body: file, contentType, fileName };
}

export type EwUploadEntity = 'ew_job' | 'ew_site';

/**
 * Upload one file against the job (`ew_job`) or, for a locate sheet, the site
 * (`ew_site`) and return its upload id. Refuses offline up front: a file cannot
 * be queued here, so it says so rather than appearing attached.
 */
export async function uploadEwFile(entityType: EwUploadEntity, entityId: string, file: File): Promise<string> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new Error('You are offline. A file can only be attached with a connection — try again when you have signal.');
  }
  const { body, contentType, fileName } = await prepareEwUpload(file);
  const presign = await api.uploads.getPresignedUrl({ fileName, contentType, entityType, entityId });
  // The uploads row is minted at presign (`uploadId`); confirm answers only the size.
  const p = (presign.data as { data?: unknown })?.data ?? presign.data;
  const d = p as { uploadUrl?: unknown; key?: unknown; uploadId?: unknown };
  if (typeof d?.uploadUrl !== 'string' || typeof d?.key !== 'string' || typeof d?.uploadId !== 'string') {
    throw new Error('The upload could not be prepared. Try again.');
  }
  await putToPresignedUrl(d.uploadUrl, body, contentType);
  await api.uploads.confirm({ key: d.key, size: body.size });
  return d.uploadId;
}

export const uploadEwJobFile = (jobId: string, file: File) => uploadEwFile('ew_job', jobId, file);

// ─── /field ──────────────────────────────────────────────────────────────────

export function useEwFieldJobs() {
  return useQuery({
    queryKey: ewFieldKeys.myJobs(),
    queryFn: async () => {
      const r = unwrapEwField<EwFieldJob[]>((await api.earthworks.field.myJobs()).data);
      return { data: Array.isArray(r.data) ? r.data : [], meta: r.meta };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwFieldJob(id: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.myJob(id ?? ''),
    queryFn: async () => unwrapEwField<EwFieldJobDetail>((await api.earthworks.field.myJob(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** That day's log (null when none written) and the day's warnings either way. */
export function useEwFieldLog(jobId: string | undefined, day: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.log(jobId ?? '', day ?? ''),
    queryFn: async () => {
      const body = (await api.earthworks.field.log(jobId as string, day as string)).data as unknown;
      const r = unwrapEwField<EwFieldLog>(body);
      const dw = (body as { day_warnings?: unknown } | null)?.day_warnings;
      const dayWarnings = r.data?.warnings ?? (Array.isArray(dw) ? (dw as EwFieldWarning[]) : []);
      return { ...r, dayWarnings };
    },
    enabled: !!jobId && !!day,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Pre-use checks that can be linked to the day's log. RE-READ ON EVERY MOUNT:
 * the log's "File a pre-use check" goes to the SHARED equipment check form and
 * comes straight back, usually inside the app-wide 30s staleTime — so without
 * this the hand returns to a cached empty list and cannot link the check they
 * just filed. The shared form does not know this key, and should not.
 */
export function useEwPreUseChecks(jobId: string | undefined, day: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.checks(jobId ?? '', day ?? ''),
    queryFn: async () => unwrapEwList<EwPreUseCheck>((await api.earthworks.preUseChecks(jobId as string, day)).data),
    enabled: !!jobId && !!day,
    refetchOnMount: 'always',
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Save the day's log. If an acknowledged warning is no longer showing (the
 * save changed the work kinds, say), the API rolls the WHOLE save back with
 * 400 `warning_not_shown`; this retries once without that kind, so the log is
 * not lost over a warning that went away.
 */
export const useSaveEwFieldLog = () =>
  useEwWrite(async ({ jobId, day, body }: { jobId: string; day: string; body: EwDailyLogWrite }): Promise<EwFieldLogSaved> => {
    const send = async (b: EwDailyLogWrite) => {
      const res = await api.earthworks.field.saveLog(jobId, day, b);
      const raw = res.data as unknown as { data?: EwFieldLog; warnings?: unknown; acknowledged?: unknown; stock_warnings?: unknown; ticket_sync?: unknown };
      return {
        data: raw?.data ?? null,
        warnings: Array.isArray(raw?.warnings) ? (raw.warnings as EwFieldLogSaved['warnings']) : [],
        acknowledged: Array.isArray(raw?.acknowledged) ? (raw.acknowledged as string[]) : [],
        created: res.status === 201,
        stock_warnings: Array.isArray(raw?.stock_warnings) ? (raw.stock_warnings as NonNullable<EwFieldLogSaved['stock_warnings']>) : [],
        ticket_sync: raw?.ticket_sync && typeof raw.ticket_sync === 'object' ? (raw.ticket_sync as EwFieldLogSaved['ticket_sync']) : null,
      };
    };
    try {
      return await send(body);
    } catch (err) {
      const e = ewApiError(err);
      const kind = typeof e.extra.kind === 'string' ? e.extra.kind : null;
      if (e.code === 'warning_not_shown' && kind && body.acknowledge_warnings?.includes(kind)) {
        return send({ ...body, acknowledge_warnings: body.acknowledge_warnings.filter((k) => k !== kind) });
      }
      throw err;
    }
  });

// ─── Office: daily logs ──────────────────────────────────────────────────────

export function useEwDailyLogs(jobId: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.logs(jobId ?? ''),
    queryFn: async () => unwrapEwList<EwDailyLogSummary>((await api.earthworks.dailyLogs.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwDailyLog(id: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.dailyLog(id ?? ''),
    queryFn: async () => unwrapEw<EwDailyLog>((await api.earthworks.dailyLogs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useDeleteEwDailyLog = () => useEwWrite(async (id: string) => (await api.earthworks.dailyLogs.remove(id)).data);

// ─── Office: rent-ins, permits, hazmat, locates ──────────────────────────────

export function useEwRentIns(jobId: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.rentIns(jobId ?? ''),
    queryFn: async () => unwrapEwList<EwRentIn>((await api.earthworks.rentIns.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
export const useCreateEwRentIn = () =>
  useEwWrite(async ({ jobId, body }: { jobId: string; body: EwRentInWrite }) => (await api.earthworks.rentIns.create(jobId, body)).data);
export const useUpdateEwRentIn = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwRentInWrite }) => (await api.earthworks.rentIns.update(id, body)).data);
export const useDeleteEwRentIn = () => useEwWrite(async (id: string) => (await api.earthworks.rentIns.remove(id)).data);

export function useEwPermits(jobId: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.permits(jobId ?? ''),
    queryFn: async () => unwrapEwList<EwPermit>((await api.earthworks.permits.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
export const useCreateEwPermit = () =>
  useEwWrite(async ({ jobId, body }: { jobId: string; body: EwPermitWrite }) => (await api.earthworks.permits.create(jobId, body)).data);
export const useDeleteEwPermit = () => useEwWrite(async (id: string) => (await api.earthworks.permits.remove(id)).data);

export function useEwHazmat(jobId: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.hazmat(jobId ?? ''),
    queryFn: async () => unwrapEwList<EwHazmat>((await api.earthworks.hazmat.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
export const useCreateEwHazmat = () =>
  useEwWrite(async ({ jobId, body }: { jobId: string; body: EwHazmatWrite }) => (await api.earthworks.hazmat.create(jobId, body)).data);
export const useDeleteEwHazmat = () => useEwWrite(async (id: string) => (await api.earthworks.hazmat.remove(id)).data);

export function useEwLocates(siteId: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.locates(siteId ?? ''),
    queryFn: async () => unwrapEwList<EwLocate>((await api.earthworks.locates.list(siteId as string)).data),
    enabled: !!siteId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
export const useCreateEwLocate = () =>
  useEwWrite(async ({ siteId, body }: { siteId: string; body: EwLocateWrite }) => (await api.earthworks.locates.create(siteId, body)).data);
export const useDeleteEwLocate = () => useEwWrite(async (id: string) => (await api.earthworks.locates.remove(id)).data);

// ─── Office: warnings ────────────────────────────────────────────────────────

export function useEwWarnings(jobId: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.warnings(jobId ?? ''),
    queryFn: async () => unwrapEwList<EwWarning>((await api.earthworks.warnings.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwWarningAcks(jobId: string | undefined) {
  return useQuery({
    queryKey: ewFieldKeys.acks(jobId ?? ''),
    queryFn: async () => unwrapEwList<EwWarningAck>((await api.earthworks.warnings.acks(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
